import { useMemo } from 'react';
import { CreditCard } from '../__autogenerated__/types/CreditCard';
import { CategoryType } from '../__autogenerated__/types/CreditCardCategoryTypes';
import { CreditCardRewardsType } from '../CreditCardRewardsTypes';
import { CreditCardData } from '../__autogenerated__/cards/CreditCardData';
import { EarnRateInfo } from '../../types/CardRewardsEarnRates';
import { flattenObject } from '../utils';
import { useCategoryEarnRatesAndCreditsV2, SelectedCategoriesMultiRate } from './useBestEarnRatesV2';

export const useBestEarnRatesByCardV2 = (
  cards: CreditCard[],
  categories: string[],
  pointValues: Record<string, number>,
  selectedCategoriesByCard?: SelectedCategoriesMultiRate,
  isPurchasingInUSA?: boolean,
  promotionalTypes?: string[]
): EarnRateInfo[][] => {
  // Use the V2 hook to get category earn rates (supports promotional types)
  const internalCategoryEarnRates = useCategoryEarnRatesAndCreditsV2(
    cards,
    categories as CategoryType[],
    pointValues,
    isPurchasingInUSA ?? true,
    selectedCategoriesByCard,
    promotionalTypes
  );

  return useMemo(() => {
    // Initialize one bucket per wallet position
    const result: EarnRateInfo[][] = cards.map(() => []);

    // For each category, find the best card instance and assign to its wallet slot
    categories.forEach((category) => {
      const categoryType = category as CategoryType;
      const categoryData = internalCategoryEarnRates[categoryType];

      if (categoryData && categoryData.sortedCardsByEarnRate.length > 0) {
        const bestCard = categoryData.sortedCardsByEarnRate[0];
        const walletIndex = bestCard.cardIndex ?? cards.indexOf(bestCard.card);

        if (walletIndex >= 0 && walletIndex < result.length) {
          result[walletIndex].push(bestCard);
        }
      }
    });

    // Sort the categories within each wallet slot for consistent ordering
    result.forEach((earnRates) => {
      earnRates.sort((a, b) => a.key.localeCompare(b.key));
    });

    return result;
  }, [cards, categories, internalCategoryEarnRates]);
};
